import { AioButtonComponent } from '@aio/ui';
import { Component, ChangeDetectionStrategy, computed, signal, inject, effect, OnInit } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthFooterComponent } from '../footer/auth-footer.component';
import { pickRandomAuthHero } from '../auth-hero.util';
import { AuthHeroPanelComponent } from '../auth-hero-panel/auth-hero-panel.component';
import { AuthService } from '../../../core/services/auth.service';
import { PermissionService } from '../../../core/services/permission.service';
import { ValidateInviteTokenRequest, SubmitNewUserPasswordRequest } from '../../../core/models/auth.models';

@Component({
  selector: 'app-create-password',
  standalone: true,
  imports: [
    AioButtonComponent,CommonModule, ReactiveFormsModule, AuthFooterComponent, AuthHeroPanelComponent],
  templateUrl: './create-password.component.html',
  styleUrls: ['./create-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreatePasswordComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly permissionService = inject(PermissionService);

  readonly hero = pickRandomAuthHero();

  // Signals for reactive state management
  readonly passwordVisible = signal(false);
  readonly confirmPasswordVisible = signal(false);
  readonly showSpecialCharError = signal(false);
  readonly passwordValue = signal('');
  readonly confirmPasswordValue = signal('');
  readonly loading = signal(false);
  readonly tokenValid = signal(false);
  readonly tokenError = signal('');
  readonly submitError = signal('');
  readonly submitSuccess = signal(false);

  // Reactive form
  readonly passwordForm: FormGroup = this.fb.group({
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: ['', [Validators.required]]
  });

  // Computed properties
  readonly password = computed(() => this.passwordValue());
  readonly confirmPassword = computed(() => this.confirmPasswordValue());

  readonly hasSpecialCharacter = computed(() => {
    const password = this.password();
    return /[!@#$%^&*(),.?":{}|<>]/.test(password);
  });

  readonly hasUppercaseLetter = computed(() => {
    const password = this.password();
    return /[A-Z]/.test(password);
  });

  readonly passwordStrength = computed(() => {
    const password = this.password();
    let score = 0;

    if (password.length >= 8) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
    if (/[A-Z]/.test(password) && /[0-9]/.test(password)) score++;

    return score;
  });

  readonly isPasswordError = computed(() =>
    this.showSpecialCharError() && (!this.hasSpecialCharacter() || !this.hasUppercaseLetter())
  );

  readonly isConfirmPasswordError = computed(() =>
    this.showSpecialCharError() && (!this.hasSpecialCharacter() || !this.hasUppercaseLetter())
  );

  readonly canSubmit = computed(() => {
    const password = this.password();
    const confirmPassword = this.confirmPassword();
    const hasValidPassword = password && password.length >= 8 && this.hasSpecialCharacter() && this.hasUppercaseLetter();
    const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

    const canSubmit = this.tokenValid() &&
           !this.loading() &&
           hasValidPassword &&
           passwordsMatch;

    return canSubmit;
  });

  private inviteToken = '';

  constructor() {
    // Listen to password field changes
    this.passwordForm.get('password')?.valueChanges.subscribe(value => {
      this.passwordValue.set(value || '');
    });

    // Listen to confirm password field changes
    this.passwordForm.get('confirmPassword')?.valueChanges.subscribe(value => {
      this.confirmPasswordValue.set(value || '');
    });
  }

  ngOnInit(): void {
    // Get activate user token from URL query params
    this.route.queryParams.subscribe(params => {
      this.inviteToken = params['activateUserToken'] || '';
      if (this.inviteToken) {
        this.validateInviteToken();
      } else {
        this.tokenError.set('No activation token provided. Please check your email for the activation link.');
      }
    });
  }

  private validateInviteToken(): void {
    this.loading.set(true);
    this.tokenError.set('');

    const request: ValidateInviteTokenRequest = {
      inviteToken: this.inviteToken
    };

    this.authService.validateInviteToken(request).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.tokenValid.set(true);
        } else {
          this.tokenError.set(response.message || 'Invalid or expired activation token.');
        }
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Activation token validation error:', error);
        console.error('Error status:', error.status);
        console.error('Error message:', error.message);
        console.error('Error response:', error.error);

        const errorMessage = error.error?.message || 'Failed to validate activation token.';
        this.tokenError.set(errorMessage);

        // If token is expired, show specific message
        if (errorMessage.toLowerCase().includes('expired')) {
          this.tokenError.set('This activation link has expired. Please contact your administrator for a new activation link.');
        }
      }
    });
  }

  onPasswordChange(): void {
    // Hide error when user starts typing and has special character and uppercase
    if (this.hasSpecialCharacter() && this.hasUppercaseLetter()) {
      this.showSpecialCharError.set(false);
    }
  }

  onPasswordBlur(): void {
    // Show error if missing special character or uppercase when leaving the field
    if (!this.hasSpecialCharacter() || !this.hasUppercaseLetter()) {
      this.showSpecialCharError.set(true);
    }
  }

  onSubmit(): void {
    if (!this.canSubmit()) {
      return;
    }

    this.loading.set(true);
    this.submitError.set('');

    const request: SubmitNewUserPasswordRequest = {
      inviteToken: this.inviteToken,
      newPassword: this.password(),
      confirmNewPassword: this.confirmPassword()
    };

    this.authService.submitNewUserPassword(request).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.submitSuccess.set(true);

          // Check if login credentials are provided for automatic login
          if (response.data?.loginToken) {
            // Handle automatic login
            this.handleAutomaticLogin(response.data);
          } else if (response.data?.mfaToken) {
            // Handle MFA flow
            this.handleMfaFlow(response.data);
          } else {
            // Fallback to login page with success message
            this.router.navigate(['/login'], {
              queryParams: {
                message: 'Password created successfully. Please login with your new credentials.'
              }
            });
          }
        } else {
          this.submitError.set(response.message || 'Failed to create password. Please try again.');
        }
      },
      error: (error) => {
        this.loading.set(false);
        const errorMessage = error.error?.message || 'An error occurred while creating your password.';
        this.submitError.set(errorMessage);

        // If token is expired, show specific message
        if (errorMessage.toLowerCase().includes('expired')) {
          this.submitError.set('This activation link has expired. Please contact your administrator for a new activation link.');
        }
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/login']);
  }

  requestNewLink(): void {
    // For activation flow, we don't have a forgot password equivalent
    // Users should contact their administrator for a new activation link
    this.router.navigate(['/login'], {
      queryParams: {
        message: 'Please contact your administrator for a new activation link.'
      }
    });
  }

  private handleAutomaticLogin(loginData: any): void {

    // Use the auth service to handle the successful login
    this.authService.handlePasswordCreationLogin(loginData);

    // Load user permissions before navigation to ensure they're available for AuthGuard
    this.permissionService.getUserPermissions().subscribe({
      next: (permissions) => {

        // Navigate after permissions are loaded
        this.router.navigate(['/dashboard']).catch(err => {
          console.error('Navigation error:', err);
        });
      },
      error: (error) => {
        console.error('Error loading user permissions:', error);
        // Still navigate even if permission loading fails
        // console.log('Navigating to dashboard (despite permission error)...');
        // this.router.navigate(['/dashboard']).catch(err => {
        //   console.error('Navigation error:', err);
        // });
      }
    });
  }

  private handleMfaFlow(loginData: any): void {

    // Store MFA token for the MFA component
    if (loginData.mfaToken) {
      // Use the auth service to store MFA token
      this.authService.storeMfaTokenForPasswordCreation(loginData.mfaToken);

      // Navigate to MFA page
      this.router.navigate(['/mfa']);
    } else {
      // Fallback to login page
      this.router.navigate(['/login'], {
        queryParams: {
          message: 'Password created successfully. Please complete MFA setup.'
        }
      });
    }
  }
}
